import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { EnrollStudentModal } from '@/components/courses/EnrollStudentModal';
import { EnrollmentStatusBadge } from '@/components/courses/EnrollmentStatusBadge';
import { usePermissions } from '@/hooks/usePermissions';
import { ApiRequestError, api, listResource } from '@/lib/api';
import {
  formatCompliancePercent,
  formatCourseDate,
  formatCourseDurationHours,
} from '@/lib/course-duration';
import { enrollmentFromCourseQuery, withSearch } from '@/lib/enrollment-navigation';

interface CourseInstructor {
  userId: string;
  user: { id: string; name: string; firstName?: string | null; lastName?: string | null };
}

interface CourseDetail {
  id: string;
  name: string;
  category?: { id: string; name: string } | null;
  primaryInstructor?: {
    id: string;
    name: string;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  instructors?: CourseInstructor[];
  minCompliancePercent?: number | string | null;
  academicHours?: number | string | null;
  startDate?: string | null;
  endDate?: string | null;
  maxEnrollment?: number | null;
  requiresEnrollment?: boolean;
  allowComments?: boolean;
  showProgress?: boolean;
  isFree?: boolean;
  price?: number | string | null;
}

interface EnrollmentRow {
  id: string;
  status?: string;
  user?: {
    id: string;
    name: string;
    fullName?: string;
    email?: string;
    avatarUrl?: string | null;
  };
}

function instructorName(user?: { name: string; firstName?: string | null; lastName?: string | null } | null) {
  if (!user) return null;
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.name;
}

function formatPrice(course: CourseDetail): string {
  if (course.isFree) return 'Gratuito';
  const value = Number(course.price);
  if (Number.isNaN(value)) return '—';
  return `$${value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

export function CourseShowPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const { hasPermission } = usePermissions();

  const canViewEnrollments = hasPermission(PERMISSIONS.INSCRIPCIONES_VER);
  const canAssignEnrollment = hasPermission(PERMISSIONS.INSCRIPCIONES_ASIGNAR);

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['courses', id],
    queryFn: () => api<CourseDetail>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data: participantsData, isLoading: participantsLoading } = useQuery({
    queryKey: ['enrollments', id, page],
    queryFn: () => listResource<EnrollmentRow>('/enrollments', { courseId: id!, page, perPage: 10 }),
    enabled: Boolean(id) && canViewEnrollments,
  });

  const participants = participantsData?.data ?? [];
  const participantsMeta = participantsData?.meta;
  const perPage = participantsMeta?.perPage ?? 10;
  const participantsTotal = participantsMeta?.total ?? participants.length;
  const participantsLastPage =
    participantsMeta?.lastPage ??
    (participantsMeta ? Math.ceil(participantsMeta.total / (participantsMeta.perPage || 10)) : 1);
  const participantsRangeStart = participantsTotal === 0 ? 0 : (page - 1) * perPage + 1;
  const participantsRangeEnd = Math.min(page * perPage, participantsTotal);

  const enrollmentQuery = id ? enrollmentFromCourseQuery(id, searchParams) : new URLSearchParams();
  const enrollmentsUrl = withSearch('/inscripciones', enrollmentQuery);

  const additionalInstructors = useMemo(() => {
    const rows = course?.instructors ?? [];
    return rows
      .map((entry) => instructorName(entry.user))
      .filter((name): name is string => Boolean(name));
  }, [course?.instructors]);

  if (!id) return null;

  return (
    <>
      <CourseHubShell
        courseId={id}
        courseName={course?.name ?? 'Curso'}
        categoryId={course?.category?.id}
        activeTab="detalle"
        description="Consulta participantes, organización y configuración de este curso."
      >
        {error && (
          <p className="mb-4 text-sm text-red-600" role="alert">
            {error instanceof ApiRequestError && error.status === 403 ? 'Sin acceso' : (error as Error).message}
          </p>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : course ? (
          <div className="lms-course-details flex flex-col gap-8">
            <section className="lms-gradebook lms-gradebook--forum">
              <div className="lms-course-forum-card">
                <div className="lms-course-forum-card__head">
                  <div className="lms-course-forum-card__heading">
                    <h2 className="lms-course-forum-card__title">Participantes del curso</h2>
                  </div>
                  {canViewEnrollments || canAssignEnrollment ? (
                    <div className="lms-course-forum-card__tools">
                      {canViewEnrollments ? (
                        <Link to={enrollmentsUrl} className="lms-course-forum-card__filter">
                          <span className="material-symbols-outlined" aria-hidden="true">
                            visibility
                          </span>
                          Ver todas las matrículas
                        </Link>
                      ) : null}
                      {canAssignEnrollment ? (
                        <button
                          type="button"
                          className="lms-course-forum-btn lms-course-forum-btn--primary"
                          onClick={() => setEnrollOpen(true)}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            person_add
                          </span>
                          Matricular
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {participantsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader />
                  </div>
                ) : participants.length === 0 ? (
                  <div className="lms-gradebook__table-wrap">
                    <div className="lms-gradebook__empty">
                      <div className="lms-gradebook__empty-icon" aria-hidden="true">
                        <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                          group_off
                        </span>
                      </div>
                      <h3>Sin participantes</h3>
                      <p>No hay participantes inscritos en este curso.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="lms-gradebook__table-wrap">
                      <table className="lms-gradebook__table">
                        <thead>
                          <tr>
                            <th className="lms-gradebook__th" scope="col">
                              Nombre completo
                            </th>
                            <th className="lms-gradebook__th" scope="col">
                              Dirección de correo
                            </th>
                            <th className="lms-gradebook__th" scope="col">
                              Estado
                            </th>
                            <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {participants.map((enrollment) => {
                            const userName = enrollment.user?.fullName ?? enrollment.user?.name ?? '—';
                            const showUrl = withSearch(`/inscripciones/${enrollment.id}`, enrollmentQuery);

                            return (
                              <tr key={enrollment.id}>
                                <th className="lms-gradebook__td" scope="row">
                                  <div className="lms-gradebook__student">
                                    {enrollment.user?.avatarUrl ? (
                                      <img src={enrollment.user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                                    ) : (
                                      <div className="lms-gradebook__avatar" aria-hidden="true">
                                        {initials(userName)}
                                      </div>
                                    )}
                                    {canViewEnrollments ? (
                                      <Link to={showUrl} className="lms-gradebook__name">
                                        {userName}
                                      </Link>
                                    ) : (
                                      <span className="lms-gradebook__name">{userName}</span>
                                    )}
                                  </div>
                                </th>
                                <td className="lms-gradebook__td">
                                  {enrollment.user?.email ? (
                                    <a href={`mailto:${enrollment.user.email}`} className="lms-gradebook__mail">
                                      {enrollment.user.email}
                                    </a>
                                  ) : (
                                    <span className="lms-gradebook__muted">—</span>
                                  )}
                                </td>
                                <td className="lms-gradebook__td">
                                  <EnrollmentStatusBadge status={enrollment.status} />
                                </td>
                                <td className="lms-gradebook__td lms-gradebook__td--actions">
                                  {canViewEnrollments ? (
                                    <Link
                                      to={showUrl}
                                      className="lms-gradebook__action"
                                      title="Ver matrícula"
                                      aria-label={`Ver matrícula de ${userName}`}
                                    >
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        visibility
                                      </span>
                                    </Link>
                                  ) : (
                                    <span className="lms-gradebook__muted">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {participantsTotal > 0 ? (
                      <div className="lms-course-forum-card__footer">
                        <span className="lms-course-forum-card__meta">
                          Mostrando <strong>{participantsRangeStart}</strong> a <strong>{participantsRangeEnd}</strong> de{' '}
                          <strong>{participantsTotal}</strong> resultados
                        </span>
                        <div className="lms-course-forum-card__pager">
                          <button
                            type="button"
                            className="lms-course-forum-card__pager-btn"
                            aria-label="Página anterior"
                            disabled={page <= 1}
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">
                              chevron_left
                            </span>
                          </button>
                          <span className="lms-course-forum-card__pager-current">{page}</span>
                          <button
                            type="button"
                            className="lms-course-forum-card__pager-btn"
                            aria-label="Página siguiente"
                            disabled={page >= participantsLastPage}
                            onClick={() => setPage((current) => Math.min(participantsLastPage, current + 1))}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">
                              chevron_right
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </section>

            <section className="lms-course-details__grid">
              <div className="lms-course-details__card">
                <div className="lms-course-details__section-title">
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    corporate_fare
                  </span>
                  <h3>Organización</h3>
                </div>
                <dl className="lms-course-details__dl">
                  <div>
                    <dt>Categoría</dt>
                    <dd>{course.category?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Instructor principal</dt>
                    <dd className="lms-course-details__instructor">
                      {course.primaryInstructor ? (
                        <>
                          <span className="lms-course-details__instructor-avatar" aria-hidden="true">
                            <span className="material-symbols-outlined text-base">person</span>
                          </span>
                          {instructorName(course.primaryInstructor)}
                        </>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Instructores adicionales</dt>
                    <dd className={additionalInstructors.length === 0 ? 'lms-course-details__muted' : undefined}>
                      {additionalInstructors.length > 0 ? additionalInstructors.join(', ') : '—'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="lms-course-details__card">
                <div className="lms-course-details__section-title">
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    school
                  </span>
                  <h3>Datos académicos</h3>
                </div>
                <dl className="lms-course-details__dl">
                  <div className="lms-course-details__dl-row">
                    <div>
                      <dt>Porcentaje mínimo</dt>
                      <dd className={course.minCompliancePercent == null ? 'lms-course-details__muted' : undefined}>
                        {formatCompliancePercent(course.minCompliancePercent) ?? '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Duración del curso</dt>
                      <dd className={course.academicHours == null ? 'lms-course-details__muted' : undefined}>
                        {course.academicHours != null
                          ? formatCourseDurationHours(course.academicHours) || '—'
                          : '—'}
                      </dd>
                    </div>
                  </div>
                  <div className="lms-course-details__dl-row">
                    <div>
                      <dt>Inicio</dt>
                      <dd className={!course.startDate ? 'lms-course-details__muted' : undefined}>
                        {formatCourseDate(course.startDate) ?? '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Finalización</dt>
                      <dd className={!course.endDate ? 'lms-course-details__muted' : undefined}>
                        {formatCourseDate(course.endDate) ?? '—'}
                      </dd>
                    </div>
                  </div>
                  <div>
                    <dt>Cupo máximo</dt>
                    <dd>{course.maxEnrollment ?? 'Sin límite'}</dd>
                  </div>
                </dl>
              </div>

              <div className="lms-course-details__card">
                <div className="lms-course-details__section-title">
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    settings_suggest
                  </span>
                  <h3>Configuración</h3>
                </div>
                <dl className="lms-course-details__config">
                  <div>
                    <dt>Inscripción</dt>
                    <dd>
                      <span className="lms-course-details__chip">
                        {course.requiresEnrollment ? 'Requerida' : 'No requerida'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Comentarios</dt>
                    <dd>
                      <span className="lms-course-details__chip">
                        {course.allowComments ? 'Permitidos' : 'No permitidos'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Progreso</dt>
                    <dd>
                      <span className="lms-course-details__chip">
                        {course.showProgress ? (
                          <>
                            <span className="material-symbols-outlined text-sm text-dash-secondary" aria-hidden="true">
                              visibility
                            </span>
                            Visible
                          </>
                        ) : (
                          'Oculto'
                        )}
                      </span>
                    </dd>
                  </div>
                  <div className="lms-course-details__config-last">
                    <dt>Precio</dt>
                    <dd>
                      <span
                        className={`lms-course-details__chip${course.isFree ? ' lms-course-details__chip--accent' : ''}`}
                      >
                        {formatPrice(course)}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>
        ) : null}
      </CourseHubShell>

      {canAssignEnrollment && id && course && (
        <EnrollStudentModal
          open={enrollOpen}
          courseId={id}
          courseName={course.name}
          onClose={() => setEnrollOpen(false)}
        />
      )}
    </>
  );
}
